import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Breadcrumbs from '../../components/Admin/Breadcrumbs';
import { Search, CheckCircle, Edit, Trash2 } from 'lucide-react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useNotifications } from '../../hooks/useNotifications';
import { useConfirm } from '../../contexts/ConfirmContext';
import EditParcelaModal from '../../components/Admin/EditParcelaModal';

interface PagamentoItem {
  _id: string;
  numeroParcela: number;
  totalParcelas: number;
  valor: number;
  vencimento: string;
  status: 'pendente' | 'pago' | 'atrasado' | 'cancelado';
  clienteId: { _id: string; nome: string };
  vendaId: { _id: string; descricao: string };
}

const STATUS_VALUES = ['pendente', 'pago', 'atrasado', 'cancelado'];

const STATUS_LABELS: { [key: string]: string } = {
  pendente: 'Pendente',
  pago: 'Pago',
  atrasado: 'Atrasado',
  cancelado: 'Cancelado'
};

const STATUS_COLORS: { [key: string]: string } = {
  pendente: 'bg-yellow-100 text-yellow-800',
  pago: 'bg-green-100 text-green-800',
  atrasado: 'bg-red-100 text-red-800',
  cancelado: 'bg-gray-100 text-gray-800'
};

interface PagamentosProps {
  embedded?: boolean;
}

const Pagamentos: React.FC<PagamentosProps> = ({ embedded = false }) => {
  const { t } = useTranslation();
  const [pagamentos, setPagamentos] = useState<PagamentoItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [editingPagamento, setEditingPagamento] = useState<PagamentoItem | null>(null);
  const notifications = useNotifications();
  const confirm = useConfirm();

  const fetchPagamentos = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: '10',
        ...(selectedStatus && { status: selectedStatus }),
      });

      const response = await axios.get(`/pagamentos?${params}`);
      setPagamentos(response.data.pagamentos);
      setTotalPages(response.data.totalPages);
    } catch (error) {
      console.error('Erro ao carregar pagamentos:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedStatus, currentPage]);

  useEffect(() => {
    fetchPagamentos();
  }, [fetchPagamentos]);

  const handleMarcarPago = async (pagamentoId: string) => {
    try {
      const response = await axios.put(`/pagamentos/${pagamentoId}/pagar`);
      setPagamentos((prev) => prev.map((p) => (p._id === pagamentoId ? response.data : p)));
      notifications.success.pagamentoRegistrado();
    } catch (error: any) {
      console.error('Erro ao marcar pagamento como pago:', error);
      notifications.error.pagamentoAction(error.response?.data?.message || 'Erro ao marcar pagamento como pago');
    }
  };

  const handleExcluirPagamento = async (pagamentoId: string) => {
    if (!(await confirm({ title: 'Excluir parcela', message: 'Esta parcela será removida permanentemente.', confirmText: 'Excluir', variant: 'danger' }))) return;
    try {
      await axios.delete(`/pagamentos/${pagamentoId}`);
      setPagamentos((prev) => prev.filter((p) => p._id !== pagamentoId));
      notifications.success.pagamentoExcluido();
    } catch (error: any) {
      console.error('Erro ao excluir pagamento:', error);
      notifications.error.pagamentoAction(error.response?.data?.message || 'Erro ao excluir parcela');
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('pt-BR', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  return (
    <div className={embedded ? '' : 'min-h-screen bg-gray-50'}>
      {!embedded && (
        <div className="bg-white shadow">
          <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
            <div className="mb-4">
              <Breadcrumbs />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{t('admin.pagamentos')}</h1>
              <p className="text-gray-600">{t('admin.pagamentosDesc')}</p>
            </div>
          </div>
        </div>
      )}

      <div className={embedded ? '' : 'max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8'}>
        <div className="bg-white rounded-lg shadow mb-6 p-6">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setSelectedStatus('')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium ${selectedStatus === '' ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            >
              Todos
            </button>
            {STATUS_VALUES.map((status) => (
              <button
                key={status}
                onClick={() => setSelectedStatus(status)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium ${selectedStatus === status ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              >
                {STATUS_LABELS[status]}
              </button>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">{t('common.loading')}</p>
            </div>
          ) : pagamentos.length === 0 ? (
            <div className="p-8 text-center">
              <div className="text-gray-400 mb-4">
                <Search className="w-16 h-16 mx-auto" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Nenhum pagamento encontrado</h3>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cliente</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Venda</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Parcela</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Valor</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vencimento</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {pagamentos.map((pagamento) => (
                    <tr key={pagamento._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        {pagamento.clienteId ? (
                          <Link to={`/admin/clientes/${pagamento.clienteId._id}`} className="text-sm text-primary-600 hover:text-primary-800">
                            {pagamento.clienteId.nome}
                          </Link>
                        ) : '—'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">{pagamento.vendaId?.descricao || '—'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{pagamento.numeroParcela}/{pagamento.totalParcelas}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">€{pagamento.valor.toFixed(2)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(pagamento.vencimento)}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${STATUS_COLORS[pagamento.status]}`}>
                          {STATUS_LABELS[pagamento.status]}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        {(pagamento.status === 'pendente' || pagamento.status === 'atrasado') && (
                          <button
                            onClick={() => handleMarcarPago(pagamento._id)}
                            className="p-2 text-green-600 hover:bg-green-100 rounded-lg transition-colors duration-200"
                            title="Marcar como pago"
                          >
                            <CheckCircle className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => setEditingPagamento(pagamento)}
                          className="p-2 text-primary-600 hover:bg-primary-100 rounded-lg transition-colors duration-200"
                          title="Editar parcela"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        {pagamento.status !== 'pago' && (
                          <button
                            onClick={() => handleExcluirPagamento(pagamento._id)}
                            className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors duration-200"
                            title="Excluir parcela"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {totalPages > 1 && (
            <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
              <div className="flex items-center justify-between">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Anterior
                  </button>
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Próxima
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Página <span className="font-medium">{currentPage}</span> de <span className="font-medium">{totalPages}</span>
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                      <button
                        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Anterior
                      </button>
                      {[...Array(totalPages)].map((_, index) => (
                        <button
                          key={index + 1}
                          onClick={() => setCurrentPage(index + 1)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            currentPage === index + 1
                              ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          {index + 1}
                        </button>
                      ))}
                      <button
                        onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                        disabled={currentPage === totalPages}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Próxima
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {editingPagamento && (
        <EditParcelaModal
          pagamento={editingPagamento}
          onClose={() => setEditingPagamento(null)}
          onSaved={(updated) =>
            setPagamentos((prev) =>
              prev.map((p) =>
                p._id === updated._id
                  ? { ...p, valor: updated.valor, vencimento: updated.vencimento, status: updated.status }
                  : p
              )
            )
          }
        />
      )}
    </div>
  );
};

export default Pagamentos;
